import { getEmailAttachments } from "../../apiCallers/graph/email/email.getters";
import { msGraphGet } from "../../apiCallers/graph/graph.general";

/**
 * Retrieves a single email by its ID, and fetches attachments if present.
 *
 * @param {string} emailId - The ID of the email to fetch.
 * @returns {Promise<Object>} A structured object containing the email and its attachments.
 * @throws {Error} If the request fails.
 */
async function getEmailWithAttachments(emailId) {
  const emailApiPath = `/me/messages/${emailId}`;
  const message = await msGraphGet(emailApiPath);

  let attachmentsData = [];
  if (message.hasAttachments) {
    attachmentsData = await getEmailAttachments(emailId);
  }

  const final_attachments = [];
  let bodyContent = message.body.content;

  // Process attachments - embed inline images, collect others
  for (const attachment of attachmentsData) {
    var isImage = attachment.contentType && attachment.contentType.startsWith("image/");
    if (isImage && attachment.contentId) {
      const cidPattern = `cid:${attachment.contentId}`;
      if (bodyContent.includes(cidPattern)) {
        // Replace CID reference with data URI for inline images
        bodyContent = bodyContent.replace(
          cidPattern,
          `data:${attachment.contentType};base64,${attachment.contentBytes}`
        );
      } else {
        final_attachments.push(attachment);
      }
    } else {
      final_attachments.push(attachment);
    }
  }

  // Update message body with embedded images
  message.body.content = bodyContent;

  return {
    email: message,
    attachments: final_attachments,
  };
}

/**
 * Constructs a search query string for case-related email searches.
 * @param {Object} single_case - The case object containing party information.
 * @returns {string} The constructed search query.
 */
const constructCaseSearchQuery = (single_case) => {
  if (!single_case) return "";

  const queryParts = [single_case.broj_predmeta, single_case.posl_br];

  // Helper to safely replace all spaces with commas
  const formatName = (name) => name ? name.replace(/ /g, ",") : "";

  // Add physical parties
  const fizStranke = single_case.connect_fiz_stranka || [];
  fizStranke.forEach((p) => {
    const osoba = p && p.fizicka_osoba ? p.fizicka_osoba : null;
    if (osoba) {
      queryParts.push(formatName(`${osoba.ime} ${osoba.prezime}`));
      queryParts.push(formatName(osoba.prezime));
    }
  });

  // Add physical counter-parties
  const fizProtu = single_case.connect_fiz_protustranka || [];
  fizProtu.forEach((p) => {
    const osoba = p && p.fizicka_osoba ? p.fizicka_osoba : null;
    if (osoba) {
      queryParts.push(formatName(`${osoba.ime} ${osoba.prezime}`));
      queryParts.push(formatName(osoba.prezime));
    }
  });

  // Add legal entity parties
  const pravStranke = single_case.prav_connect_stranka || [];
  pravStranke.forEach((p) => {
    if (p && p.pravna_osoba && p.pravna_osoba.ime) {
      queryParts.push(formatName(p.pravna_osoba.ime));
    }
  });

  // Add legal entity counter-parties
  const pravProtu = single_case.prav_connect_protustranka || [];
  pravProtu.forEach((p) => {
    if (p && p.pravna_osoba && p.pravna_osoba.ime) {
      queryParts.push(formatName(p.pravna_osoba.ime));
    }
  });

  return queryParts.filter(Boolean).join(" OR ") + " OR ";
};

/**
 * Constructs a folder search query string for case-related searches.
 * @param {Object} single_case - The case object containing party information.
 * @returns {string} The constructed folder search query.
 */
const constructCaseFolderSearchQuery = (single_case) => {
  if (!single_case) return "";

  const queryParts = [];

  // Add physical parties
  const fizStranke = single_case.connect_fiz_stranka || [];
  fizStranke.forEach((p) => {
    const osoba = p && p.fizicka_osoba ? p.fizicka_osoba : null;
    if (osoba) {
      queryParts.push(`${osoba.ime} ${osoba.prezime}`);
    }
  });

  // Add physical counter-parties
  const fizProtu = single_case.connect_fiz_protustranka || [];
  fizProtu.forEach((p) => {
    const osoba = p && p.fizicka_osoba ? p.fizicka_osoba : null;
    if (osoba) {
      queryParts.push(`${osoba.ime} ${osoba.prezime}`);
    }
  });

  // Add legal entity parties
  const pravStranke = single_case.prav_connect_stranka || [];
  pravStranke.forEach((p) => {
    if (p && p.pravna_osoba && p.pravna_osoba.ime) {
      queryParts.push(p.pravna_osoba.ime);
    }
  });

  // Add legal entity counter-parties
  const pravProtu = single_case.prav_connect_protustranka || [];
  pravProtu.forEach((p) => {
    if (p && p.pravna_osoba && p.pravna_osoba.ime) {
      queryParts.push(p.pravna_osoba.ime);
    }
  });

  return queryParts.join("+");
};

const mapSearchApiResponse = (search_api_hits) => {
  return search_api_hits.map((p) => {
    return {
      subject: p.resource.subject,
      id: p.hitId,
      sender: p.resource.from,
      hasAttachments: p.resource.hasAttachments,
      createdDateTime: p.resource.createdDateTime,
    };
  });
};

/**
 * Filters and maps email addresses from search results.
 * @param {string} srch_txt - The search text to filter by.
 * @param {Array} srch_arr - The search results array.
 * @returns {Array<string>} Array of matching email addresses.
 */
const filterAndMapEmails = (srch_txt = "", srch_arr = []) => {
  const searchLower = srch_txt.toLowerCase();
  const resources = srch_arr.map((p) => p.resource).filter(Boolean);

  // Collect all email addresses from different fields
  const emailAddresses = [];

  resources.forEach((resource) => {
    if (resource.from && resource.from.emailAddress && resource.from.emailAddress.address) {
      emailAddresses.push(resource.from.emailAddress);
    }
    if (resource.replyTo && resource.replyTo.emailAddress && resource.replyTo.emailAddress.address) {
      emailAddresses.push(resource.replyTo.emailAddress);
    }
    if (resource.sender && resource.sender.emailAddress && resource.sender.emailAddress.address) {
      emailAddresses.push(resource.sender.emailAddress);
    }
  });

  // Filter by search text and return unique addresses
  return emailAddresses
    .filter((item) => {
      var nameMatch = item.name && item.name.toLowerCase().includes(searchLower);
      var addressMatch = item.address && item.address.toLowerCase().includes(searchLower);
      return nameMatch || addressMatch;
    })
    .map((p) => p.address);
};

const removeRedundantEmail = (all_email_list)=>{
  return  [...new Set(all_email_list)].filter(p=> p.includes("@"));
}


export {
  getEmailWithAttachments,
  constructCaseSearchQuery,
  constructCaseFolderSearchQuery,
  mapSearchApiResponse,
  filterAndMapEmails,
  removeRedundantEmail
};
